import { supabase } from '@/lib/supabase'
import type {
  SpaceContributionRow,
  SpaceContributionInsert,
  SpaceContributionUpdate,
  SpaceContributionTableRow,
  SpaceContributionFilters,
} from '@/types/space-contributions'

/**
 * Fetch all space contributions for an account with optional filtering
 */
export async function fetchSpaceContributions(
  organizationId: string,
  filters?: SpaceContributionFilters
): Promise<SpaceContributionTableRow[]> {
  let query = supabase
    .from('space_contributions')
    .select(
      `id,
      organization_id,
      location_id,
      unit_id,
      source_type,
      aircraft_id,
      flight_schedule_id,
      lease_contract_id,
      available_from,
      available_until,
      sqft,
      wingspan,
      length,
      status,
      created_at,
      updated_at,
      units:unit_id(unit_code, name, building_id, buildings:building_id(name)),
      aircraft:aircraft_id(tail_number, aircraft_types:aircraft_type_id(model)),
      flight_schedules:flight_schedule_id(status)`
    )
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .order('available_from', { ascending: false })

  // Apply filters
  if (filters?.search) {
    query = query.or(
      `hangar_units.name.ilike.%${filters.search}%,aircraft.tail_number.ilike.%${filters.search}%`
    )
  }

  if (filters?.sourceType) {
    query = query.eq('source_type', filters.sourceType)
  }

  if (filters?.status) {
    query = query.eq('status', filters.status)
  }

  if (filters?.unitId) {
    query = query.eq('unit_id', filters.unitId)
  }

  if (filters?.dateFrom) {
    query = query.gte('available_from', filters.dateFrom)
  }

  if (filters?.dateTo) {
    query = query.lte('available_until', filters.dateTo)
  }

  const { data, error } = await query

  if (error) throw error

  // Map the response to match SpaceContributionTableRow
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?.map((row: any) => ({
      id: row.id,
      organization_id: row.organization_id,
      location_id: row.location_id,
      unit_id: row.unit_id,
      source_type: row.source_type,
      unit_name: row.units?.name || row.units?.unit_code,
      hangar_name: row.units?.buildings?.name,
      aircraft_id: row.aircraft_id,
      tail_number: row.aircraft?.tail_number,
      aircraft_type_name: row.aircraft?.aircraft_types?.model,
      flight_schedule_id: row.flight_schedule_id,
      lease_contract_id: row.lease_contract_id,
      available_from: row.available_from,
      available_until: row.available_until,
      sqft: row.sqft,
      wingspan: row.wingspan,
      length: row.length,
      status: row.status,
      created_at: row.created_at,
      updated_at: row.updated_at,
    })) || []
  )
}

/**
 * Fetch a single space contribution by ID
 */
export async function fetchSpaceContribution(id: string): Promise<SpaceContributionRow> {
  const { data, error } = await supabase
    .from('space_contributions')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data as SpaceContributionRow
}

/**
 * Create a new space contribution
 */
export async function createSpaceContribution(
  data: SpaceContributionInsert
): Promise<SpaceContributionRow> {
  const { data: created, error } = await supabase
    .from('space_contributions')
    .insert({
      organization_id: data.organization_id,
      location_id: data.location_id,
      unit_id: data.unit_id,
      source_type: data.source_type,
      aircraft_id: data.aircraft_id ?? null,
      flight_schedule_id: data.flight_schedule_id ?? null,
      lease_contract_id: data.lease_contract_id ?? null,
      available_from: data.available_from,
      available_until: data.available_until,
      sqft: data.sqft,
      wingspan: data.wingspan ?? null,
      length: data.length ?? null,
      position_x: data.position_x ?? null,
      position_y: data.position_y ?? null,
      status: data.status ?? 'projected',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
    .select()
    .single()

  if (error) throw error
  return created as SpaceContributionRow
}

/**
 * Update an existing space contribution
 */
export async function updateSpaceContribution(
  id: string,
  data: SpaceContributionUpdate
): Promise<SpaceContributionRow> {
  const updateData: Record<string, unknown> = {}

  if (data.unit_id !== undefined) updateData.unit_id = data.unit_id
  if (data.source_type !== undefined) updateData.source_type = data.source_type
  if (data.aircraft_id !== undefined) updateData.aircraft_id = data.aircraft_id
  if (data.flight_schedule_id !== undefined)
    updateData.flight_schedule_id = data.flight_schedule_id
  if (data.lease_contract_id !== undefined)
    updateData.lease_contract_id = data.lease_contract_id
  if (data.available_from !== undefined) updateData.available_from = data.available_from
  if (data.available_until !== undefined) updateData.available_until = data.available_until
  if (data.sqft !== undefined) updateData.sqft = data.sqft
  if (data.wingspan !== undefined) updateData.wingspan = data.wingspan
  if (data.length !== undefined) updateData.length = data.length
  if (data.position_x !== undefined) updateData.position_x = data.position_x
  if (data.position_y !== undefined) updateData.position_y = data.position_y
  if (data.status !== undefined) updateData.status = data.status

  const { data: updated, error } = await supabase
    .from('space_contributions')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return updated as SpaceContributionRow
}

/**
 * Soft delete a space contribution (sets deleted_at timestamp)
 */
export async function deleteSpaceContribution(id: string): Promise<void> {
  const { error } = await supabase
    .from('space_contributions')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}
